// Mark stack reducer. ADR 0014 §2.3.3 / §2.8 / 軸 1 §A.2 §G.
//
// `mark.<element>` 系は「1 stack ごとに独立タイマー、refresh で残時間延長、
// 属性別 decayTicks」という他の status 群とは異なるセマンティクスを持つ。
// BuffStack.perStackExpiresAt? に各 stack の expire tick を保持し、`tickMarkDecay`
// が毎 tick 個別 expire を処理する (一括 expire の既存 `maintainBuffs` と独立)。
//
// `tickMarkDecay` は `tick.ts` Step 1 (`maintainBuffs` の前) に挿入される。
// stack が個別 expire したら `expiresAt = tick` に詰めて既存 `maintainBuffs` の
// drop に乗せる (二重実装回避)。
import { STATUS_EFFECT_DEFS } from '@renavora/shared';
/**
 * Apply (refresh / append) one mark stack to `target` with the
 * configured per-element decay duration from STATUS_EFFECT_DEFS.
 *
 * Semantics (ADR 0014 §2.3.3, 軸 1 §A.2):
 *   - 新規付与 → 1 stack 追加 + 該当 expire tick を `perStackExpiresAt` に append
 *   - 既存 stack 数 < maxStacks → stack +1 + append
 *   - 既存 stack 数 = maxStacks → 最古を捨てて新規を append (refresh)
 *   - `expiresAt` は常に `max(perStackExpiresAt)` を維持 (legacy 互換)
 *
 * `markId` が `STATUS_EFFECT_DEFS` に未定義 (= 既存 42 種への誤呼び出し) の場合は
 * no-op (event log には何も記録しない)。catalogue 投入 (ADR 0015) で defined
 * になる想定。
 */
export function applyMarkStack(target, markId, tick, state, sourceId) {
    const def = STATUS_EFFECT_DEFS[markId];
    if (def === undefined)
        return;
    const decayTicks = def.durationTicks;
    const maxStacks = def.maxStacks;
    const expireAt = tick + decayTicks;
    const existing = target.debuffs.find((b) => b.id === markId);
    if (existing !== undefined) {
        const arr = existing.perStackExpiresAt ?? [];
        if (existing.stacks < maxStacks) {
            // 新規 1 stack 追加
            existing.stacks += 1;
            existing.perStackExpiresAt = [...arr, expireAt];
        }
        else {
            // 上限到達 → 最古を捨てて新規を append (refresh セマンティクス)
            existing.perStackExpiresAt = [...arr.slice(1), expireAt];
            // stacks は据え置き (maxStacks のまま)
        }
        existing.expiresAt = Math.max(...existing.perStackExpiresAt);
    }
    else {
        target.debuffs.push({
            id: markId,
            stacks: 1,
            expiresAt: expireAt,
            perStackExpiresAt: [expireAt],
        });
    }
    state.events.push({
        kind: 'statusApplied',
        tick,
        sourceId,
        targetId: target.id,
        statusId: markId,
        stacks: 1,
    });
}
/**
 * Per-tick natural decay for any BuffStack carrying `perStackExpiresAt`.
 * Removes individual expired stacks while keeping the remaining ones alive
 * with their own timers.
 *
 * ADR 0014 §2.3.3 / 軸 1 §A.2 §G。
 *
 * - `perStackExpiresAt` 未設定 → 既存 `maintainBuffs` が単一 `expiresAt` で
 *   一括 drop する経路に委ねる (本関数は no-op)
 * - `perStackExpiresAt` 設定 → 個別 expire を判定し、残り stack を保持
 *   - 全 stack 個別 expire 済 → `expiresAt = tick` に詰めて、後続の
 *     `maintainBuffs` (tick.ts Step 1) で drop されるよう仕向ける
 *   - 一部残存 → `expiresAt = max(remaining)` を維持、`stacks = remaining.length`
 *
 * 呼び出し位置: `tick.ts` Step 1 (`maintainBuffs` の **前**)。
 */
export function tickMarkDecay(state) {
    const tick = state.tick;
    for (const entity of state.entities) {
        if (entity.hp <= 0)
            continue;
        for (const list of [entity.debuffs, entity.buffs]) {
            for (const buff of list) {
                if (buff.perStackExpiresAt === undefined)
                    continue;
                const remaining = buff.perStackExpiresAt.filter((t) => t > tick);
                if (remaining.length === buff.perStackExpiresAt.length)
                    continue;
                if (remaining.length === 0) {
                    // 全 stack expire → 既存 maintainBuffs が drop してくれるよう詰める
                    buff.perStackExpiresAt = remaining;
                    buff.stacks = 0;
                    buff.expiresAt = tick;
                }
                else {
                    buff.perStackExpiresAt = remaining;
                    buff.stacks = remaining.length;
                    buff.expiresAt = Math.max(...remaining);
                }
            }
        }
    }
}
//# sourceMappingURL=marks.js.map